import { useCallback, useMemo, useContext, useState } from "react"
import { includes } from "lodash"
import { DeepReadonly } from "ts-essentials"
import { Button } from "@mui/material"
import { Tonality } from "@mui/icons-material"
import InstrumentSelector from "connected-components/instrument-selector"
import { useModelMetadata } from "resources/models"
import { LOADING, ERROR } from "helpers/data-statuses"
import Loading from "components/loading"
import Error from "components/error"
import { isETFBaseUnsupported, ETF_BASE_UNSUPPORTED_MESSAGE, makeFindAndGetBaseMetadata } from "helpers/base"
import { IDashboardContext } from "connected-components/dashboard/context/types"
import useOpenUnifiedSearch from "connected-components/dashboard/hooks/use-open-unified-search"
import { InstrumentMetadata } from "helpers/types/instruments/metadata"
import { SelectedInstrument } from "helpers/types/instruments/selected"
import { RECENT_INSTRUMENT_SEARCH_TYPES, useActiveInstruments } from "resources/instruments"
import { useDefaultSelectedBase } from "../dashboard/hooks/use-selected-base"

import "./style.scss"

export default function BaseSelector({ context, baseInstrumentMetadata, mainInstrumentMetadata, onBaseSelected }: DeepReadonly<BaseSelectorProps>) {
  const { modelId, setBase, base = null, isEndDateIntraday } = useContext(context)

  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)
  const [lastSelectedInstrument, setLastSelectedInstrument] = useState(base)

  const defaultSelectedBase = useDefaultSelectedBase(modelId, lastSelectedInstrument)

  const modelSupportsComposites = useMemo(
    () => !includes(modelMetadata?.missingDataFiles, "composite"),
    [modelMetadata],
  )

  const handleSetSelectedBase = useCallback((base: SelectedInstrument | null, isDefault = false) => {
    if (base) {
      setBase(base, mainInstrumentMetadata)
      setLastSelectedInstrument(base)
    } else {
      setBase(null)
    }
    onBaseSelected?.(base, isDefault)
  }, [setBase, mainInstrumentMetadata, onBaseSelected])

  const openSearch = useOpenUnifiedSearch(context, handleSetSelectedBase, RECENT_INSTRUMENT_SEARCH_TYPES.BASE, null, true, true)

  const [activeInstruments] = useActiveInstruments(modelId)

  const handleToggleActive = useCallback(() => {
    const isDefaultBaseAvailable = defaultSelectedBase && activeInstruments && makeFindAndGetBaseMetadata(null, activeInstruments)(defaultSelectedBase)
    if (isDefaultBaseAvailable) {
      handleSetSelectedBase(defaultSelectedBase, true)
    } else {
      openSearch()
    }
  }, [handleSetSelectedBase, defaultSelectedBase, activeInstruments, openSearch])

  const handleToggleInactive = useCallback(() => {
    handleSetSelectedBase(null)
  }, [handleSetSelectedBase])

  const lightningBoltMessage = useMemo(() => {
    if (isEndDateIntraday) {
      return "Displaying absolute metrics, benchmark data is not available with intraday dates"
    }
    if (isETFBaseUnsupported(baseInstrumentMetadata, modelSupportsComposites)) {
      return ETF_BASE_UNSUPPORTED_MESSAGE
    }
  }, [isEndDateIntraday, baseInstrumentMetadata, modelSupportsComposites])

  if (modelMetadataStatus === LOADING) {
    return <div className="connected_components_base-selector-status"><Loading /></div>
  }

  if (modelMetadataStatus === ERROR) {
    return <div className="connected_components_base-selector-status"><Error /></div>
  }

  return <div className="connected_components_base-selector">
    {baseInstrumentMetadata ? (
      <InstrumentSelector
        context={context}
        openSearch={openSearch}
        icon={<Tonality />}
        labelPrefix="Active to "
        selectedInstrumentMetadata={baseInstrumentMetadata}
        lightningBoltMessage={lightningBoltMessage}
        onClear={handleToggleInactive}
      />
    ) : (
      <Button
        variant="text"
        onClick={handleToggleActive}
        className="search-benchmark-button"
        startIcon={<Tonality />}
      >
        <div className="search-benchmark-label-container">
          <span>Benchmark</span>
        </div>
      </Button>
    )}
  </div>
}

interface BaseSelectorProps {
  context: IDashboardContext
  mainInstrumentMetadata: InstrumentMetadata
  baseInstrumentMetadata?: InstrumentMetadata
  onBaseSelected?: (base: SelectedInstrument | null, isDefault: boolean) => void
}
